import {
  PHOTO_PRINT_CAPTION_STYLE,
  type PhotoPrintDraft,
} from './PhotoPrintFlowTypes';
import {
  PHOTO_PRINT_EXPORT_HEIGHT,
  PHOTO_PRINT_EXPORT_WIDTH,
} from './PhotoPrintPrintSpec';

export function getPhotoPrintCaptionStyle(textStyle: PhotoPrintDraft['textStyle']) {
  switch (textStyle) {
    case PHOTO_PRINT_CAPTION_STYLE.CLASSIC:
      return PHOTO_PRINT_CAPTION_STYLE.CLASSIC;
    case PHOTO_PRINT_CAPTION_STYLE.TYPEWRITER:
      return PHOTO_PRINT_CAPTION_STYLE.TYPEWRITER;
    case PHOTO_PRINT_CAPTION_STYLE.HANDWRITING:
    default:
      return PHOTO_PRINT_CAPTION_STYLE.HANDWRITING;
  }
}

export async function createRenderedPhotoPrintImage(draft: PhotoPrintDraft) {
  const source = draft.editedImage || draft.originalImage;
  if (!source?.startsWith('data:image/')) return null;

  const image = await loadImage(source);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;

  canvas.width = PHOTO_PRINT_EXPORT_WIDTH;
  canvas.height = PHOTO_PRINT_EXPORT_HEIGHT;

  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.filter = getRenderedPhotoFilter(draft);
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((draft.crop.rotation * Math.PI) / 180);
  const zoom = draft.zoom > 0 ? draft.zoom : 1;
  context.scale(draft.crop.flipped ? -zoom : zoom, zoom);

  const drawSize = getFrameDrawSize(
    image.naturalWidth,
    image.naturalHeight,
    canvas.width,
    canvas.height,
    draft.crop.aspectRatio,
  );
  context.drawImage(
    image,
    -drawSize.width / 2,
    -drawSize.height / 2,
    drawSize.width,
    drawSize.height,
  );

  return canvas.toDataURL('image/jpeg', 0.92);
}

export async function createCompressedPhotoPrintFile(imageDataUrl: string | null) {
  if (!imageDataUrl || !imageDataUrl.startsWith('data:image/')) {
    return null;
  }

  const image = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return dataUrlToFile(imageDataUrl, 'photo-print.jpg');
  }

  const attempts = [
    { maxDimension: 1500, quality: 0.78 },
    { maxDimension: 1280, quality: 0.72 },
    { maxDimension: 1100, quality: 0.66 },
    { maxDimension: 960, quality: 0.6 },
  ];
  const targetBytes = 850 * 1024;
  let fallback = imageDataUrl;

  for (const attempt of attempts) {
    const { width, height } = getResizedDimensions(
      image.naturalWidth,
      image.naturalHeight,
      attempt.maxDimension,
    );

    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const compressed = canvas.toDataURL('image/jpeg', attempt.quality);
    fallback = compressed;

    if (getDataUrlByteSize(compressed) <= targetBytes) {
      return dataUrlToFile(compressed, 'photo-print.jpg');
    }
  }

  return dataUrlToFile(fallback, 'photo-print.jpg');
}

export async function buildPhotoPrintEditorFile(draft: PhotoPrintDraft) {
  const renderedPhoto = await createRenderedPhotoPrintImage(draft);
  const dataUrl = renderedPhoto || draft.editedImage || draft.originalImage;
  const file = await createCompressedPhotoPrintFile(dataUrl);
  return { file, dataUrl };
}

function getRenderedPhotoFilter(draft: PhotoPrintDraft) {
  const filterParts = [
    getRenderedBaseFilter(draft.filter),
    `brightness(${100 + draft.brightness}%)`,
    `contrast(${100 + draft.contrast}%)`,
    `saturate(${100 + draft.saturation}%)`,
  ].filter(Boolean);

  return filterParts.join(' ');
}

function getRenderedBaseFilter(filter: PhotoPrintDraft['filter']) {
  switch (filter) {
    case 'vintage':
      return 'sepia(0.75) contrast(0.95) saturate(0.8)';
    case 'warm':
      return 'sepia(0.35) hue-rotate(-12deg) saturate(1.25)';
    case 'cool':
      return 'hue-rotate(20deg) saturate(0.85) contrast(1.08)';
    case 'bw':
      return 'grayscale(1)';
    case 'original':
    default:
      return '';
  }
}

function getFrameDrawSize(
  imageWidth: number,
  imageHeight: number,
  frameWidth: number,
  frameHeight: number,
  aspectRatio: PhotoPrintDraft['crop']['aspectRatio'],
) {
  const scale =
    aspectRatio === 'fit'
      ? Math.min(frameWidth / imageWidth, frameHeight / imageHeight)
      : Math.max(frameWidth / imageWidth, frameHeight / imageHeight);

  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
  };
}

function dataUrlToFile(dataUrl: string, filename: string) {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);base64/)?.[1] || 'image/jpeg';
  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mime });
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth === 0 || image.naturalHeight === 0) {
        reject(new Error('Image decoded with zero dimensions'));
        return;
      }
      resolve(image);
    };
    image.onerror = reject;
    image.src = src;
  });
}

function getResizedDimensions(width: number, height: number, maxDimension: number) {
  const largestSide = Math.max(width, height);
  if (largestSide <= maxDimension) {
    return { width, height };
  }

  const ratio = maxDimension / largestSide;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function getDataUrlByteSize(dataUrl: string) {
  const base64 = dataUrl.split(',')[1] || '';
  return Math.ceil((base64.length * 3) / 4);
}
